// pages/routing/RoutingChangeSets.jsx — SOUND-827 Phase 2
//
// The "Changes" tab on /routing/:companyId: this client's change sets, newest
// first (the API caps the list at 50). A row opens the review page.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GitPullRequest } from "lucide-react";
import RecordTable from "components/Records/RecordTable";
import RelativeTime from "components/RelativeTime";
import { useRoutingChangeSets } from "hooks/useRouting";
import { effectiveSetStatus, setStatusChip, changeCountsSummary } from "utilities/routingChanges";

const STATUS_FILTERS = [
  ["", "All statuses"],
  ["pending_approval", "Pending approval"],
  ["applied", "Applied"],
  ["partially_applied", "Partially applied"],
  ["failed", "Failed"],
  ["declined", "Declined"],
  ["expired", "Expired"],
];

export default function RoutingChangeSets({ companyId }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const { changeSets, loading, error, refetch } = useRoutingChangeSets(companyId, status || undefined);

  const columns = [
    {
      key: "status",
      header: "Status",
      width: 160,
      exportValue: (cs) => setStatusChip(effectiveSetStatus(cs)).label,
      cell: (cs) => {
        const chip = setStatusChip(effectiveSetStatus(cs));
        return <span className={`badge ${chip.badge}`}>{chip.label}</span>;
      },
    },
    {
      key: "title",
      header: "Title",
      width: "30%",
      exportValue: (cs) => cs.title,
      cell: (cs) => (
        <div className="min-w-0">
          <div className="truncate font-medium">{cs.title || "Untitled"}</div>
          {cs.rollbackOf && <div className="truncate text-xs text-muted-foreground">Rollback</div>}
        </div>
      ),
    },
    {
      key: "author",
      header: "Author",
      width: 200,
      exportValue: (cs) => cs.author?.email || cs.author?.service || "",
      cell: (cs) => (
        <span className="text-muted-foreground">
          {cs.author?.email || cs.author?.service || (cs.source === "model" ? "Model" : "—")}
        </span>
      ),
    },
    {
      key: "createdAt",
      header: "Created",
      width: 120,
      cell: (cs) => <RelativeTime value={cs.createdAt} />,
    },
    {
      key: "counts",
      header: "Changes",
      width: "28%",
      exportValue: changeCountsSummary,
      cell: (cs) => <span className="text-muted-foreground">{changeCountsSummary(cs) || "—"}</span>,
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <select
          className="select w-auto text-sm"
          aria-label="Filter change sets by status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {STATUS_FILTERS.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      </div>
      <RecordTable
        columns={columns}
        rows={changeSets}
        rowKey={(cs) => cs.changeSetId}
        onRowClick={(cs) => navigate(`/routing/${companyId}/changes/${cs.changeSetId}`)}
        loading={loading}
        error={error}
        onRetry={refetch}
        filtered={!!status}
        skeletonRows={4}
        empty={{
          icon: GitPullRequest,
          heading: "No change sets yet",
          description: "Turn on \"Propose changes\" to draft edits to this client's routing.",
        }}
        noMatch={{ heading: "No change sets with this status" }}
      />
    </div>
  );
}
